import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * PageEvent matches the shape of Angular Material's PageEvent
 * so existing event handlers require zero changes.
 */
export interface PageEvent {
  pageIndex: number;
  pageSize: number;
  length: number;
}

/**
 * PaginatorComponent -- drop-in replacement for mat-paginator.
 *
 * Usage:
 *   <app-paginator
 *     [length]="totalItems"
 *     [pageSize]="pageSize"
 *     [pageIndex]="pageIndex"
 *     [pageSizeOptions]="[10, 25, 50]"
 *     (page)="onPage($event)">
 *   </app-paginator>
 */
@Component({
  selector: 'app-paginator',
  standalone: false,
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss']
})
export class PaginatorComponent {
  @Input() length = 0;
  @Input() pageSize = 10;
  @Input() pageIndex = 0;
  @Input() pageSizeOptions: number[] = [5, 10, 25];

  @Output() page = new EventEmitter<PageEvent>();

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.length / this.pageSize));
  }

  get rangeStart(): number {
    return this.length === 0 ? 0 : this.pageIndex * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.length);
  }

  get hasPreviousPage(): boolean {
    return this.pageIndex > 0;
  }

  get hasNextPage(): boolean {
    return this.pageIndex < this.totalPages - 1;
  }

  previousPage(): void {
    if (this.hasPreviousPage) {
      this.pageIndex--;
      this.emitPageEvent();
    }
  }

  nextPage(): void {
    if (this.hasNextPage) {
      this.pageIndex++;
      this.emitPageEvent();
    }
  }

  onPageSizeChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.pageSize = Number(select.value);
    this.pageIndex = 0; // Reset to first page when page size changes
    this.emitPageEvent();
  }

  private emitPageEvent(): void {
    this.page.emit({
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.length
    });
  }
}
